import { useCallback, useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Icon, IconButton } from "@livingmap/hippo";

import { MapContext } from "../../Map";

import EnGBKeyboard from "./components/EnGBKeyboard/EnGBKeyboard";
import HrHRKeyboard from "./components/HrHRKeyboard/HrHRKeyboard";
import ZhCNKeyboard from "./components/ZhCNKeyboard/ZhCNKeyboard";
import EsPEKeyboard from "./components/EsPEKeyboard/EsPEKeyboard";
import ElGRKeyboard from "./components/ElGRKeyboard/ElGRKeyboard";
import NumericKeyboard from "./components/NumericKeyboard/NumericKeyboard";
import SymbolKeyboard from "./components/SymbolKeyboard/SymbolKeyboard";
import ArSAKeyboard from "./components/ArSAKeyboard/ArSAKeyboard";
import ZhHKKeyboard from "./components/ZhHKKeyboard/ZhHKKeyboard";

import LanguageSwitchButton from "./components/LanguageSwitchButton/LanguageSwitchButton";

import styles from "./Keyboard.module.scss";

interface KeyboardComponentProps {
  handleClick: (...args: any[]) => void;
  handleBackspace: (...args: any[]) => void;
  handleKeyboardTypeSwitch?: (type: KeyboardType) => void;
  handleSearch: () => void;
  handleReplace?: (oldText: string, newText: string) => void;
  languageSwitchButton?: React.ReactNode;
  showSearchKey?: boolean;
}

const keyboardRegistry: Record<string, React.FC<KeyboardComponentProps>> = {
  "en-GB": EnGBKeyboard,
  "hr-HR": HrHRKeyboard,
  "zh-CN": ZhCNKeyboard,
  "es-PE": EsPEKeyboard,
  "el-GR": ElGRKeyboard,
  "ar-SA": ArSAKeyboard,
  "zh-HK": ZhHKKeyboard,
};

const getKeyboardComponent = (
  key: string,
): React.FC<KeyboardComponentProps> => {
  return keyboardRegistry[key] || EnGBKeyboard;
};

export interface KeyboardProps {
  dataQA: string;
  project: string | null;
  onChange: (inputValue: string) => void;
  onClose: () => void;
  enableInput: boolean;
  searchTerm?: string;
  onSearch?: (searchTerm: string) => void;
  languageKey?: string;
  isLatinLanguage?: boolean;
  showSearchKey?: boolean;
}

export type KeyboardType = "text" | "numeric" | "symbol";

const Keyboard: React.FC<KeyboardProps> = ({
  dataQA,
  project,
  onChange,
  onClose,
  enableInput,
  searchTerm,
  onSearch,
  languageKey = "en-GB",
  isLatinLanguage,
  showSearchKey = true,
}) => {
  const { t } = useTranslation();
  const { controlTheme } = useContext(MapContext);

  const inputField = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState("");

  const [keyboardType, setKeyboardType] = useState<KeyboardType>("text");
  const [activeKeyboardLanguage, setActiveKeyboardLanguage] =
    useState(languageKey);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const inputCharacters = inputValue.split("");

    // Limit input to 1000 characters
    if (inputCharacters.length >= 1000) return;

    // Input has been disabled from external source
    if (!enableInput) return;
    inputCharacters.push(e.currentTarget.name);
    setInputValue(inputCharacters.join(""));
  };

  const handleKeyboardTypeSwitch = (type: KeyboardType) => {
    setKeyboardType(type);
  };

  const handleBackspace = () => {
    const inputCharacters = inputValue.split("");
    if (inputCharacters.length > 0) {
      inputCharacters.pop();
      setInputValue(inputCharacters.join(""));
    }
  };

  const handleReplace = (oldText: string, newText: string) => {
    // Replace the last occurrence of oldText with newText
    const lastIndex = inputValue.lastIndexOf(oldText);
    if (lastIndex !== -1) {
      const newValue =
        inputValue.substring(0, lastIndex) +
        newText +
        inputValue.substring(lastIndex + oldText.length);
      setInputValue(newValue);
    }
  };

  const handleEventPropagation = (
    e:
      | React.MouseEvent<HTMLInputElement, MouseEvent>
      | React.TouchEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFocus = useCallback(() => {
    if (!inputField.current || !inputValue) return;
    inputField.current.focus();
    inputField.current.setSelectionRange(inputValue.length, inputValue.length);
    inputField.current.scrollLeft = inputField.current.scrollWidth;
  }, [inputValue]);

  const handleSearch = () => {
    if (onSearch) onSearch(inputValue);
  };

  const handleLanguageSwitch = () => {
    setActiveKeyboardLanguage((prev) =>
      prev === "en-GB" ? languageKey : "en-GB",
    );
  };

  useEffect(() => {
    inputField.current?.setSelectionRange(inputValue.length, inputValue.length);
    handleFocus();
  }, [inputValue, handleFocus]);

  useEffect(() => {
    onChange(inputValue);
  }, [inputValue, onChange]);

  useEffect(() => {
    if (searchTerm) setInputValue(searchTerm);
  }, [searchTerm]);

  return (
    <div
      data-qa={dataQA}
      className={classNames(styles.container, styles[controlTheme])}
      onClick={(e) => e.stopPropagation()}
    >
      <Icon
        name="search"
        size={24}
        dataQA="search-icon"
        className={styles.searchIcon}
      />
      <input
        ref={inputField}
        inputMode="none"
        type="text"
        placeholder={t("keyboard.input_placeholder", { project })}
        value={inputValue}
        className={styles.input}
        onMouseDown={handleEventPropagation}
        onTouchEnd={handleEventPropagation}
        onChange={handleEventPropagation}
        onBlur={handleFocus}
        spellCheck={false}
      />
      {/* We don't want to apply RTL styles to the keyboard */}
      <div dir="ltr">
        {keyboardType === "text" ? (
          (() => {
            const KeyboardComponent = getKeyboardComponent(
              activeKeyboardLanguage,
            );
            return (
              <KeyboardComponent
                handleClick={handleClick}
                handleBackspace={handleBackspace}
                handleKeyboardTypeSwitch={handleKeyboardTypeSwitch}
                handleSearch={handleSearch}
                handleReplace={handleReplace}
                languageSwitchButton={
                  !isLatinLanguage ? (
                    <LanguageSwitchButton onClick={handleLanguageSwitch} />
                  ) : null
                }
                showSearchKey={showSearchKey}
              />
            );
          })()
        ) : keyboardType === "numeric" ? (
          <NumericKeyboard
            handleClick={handleClick}
            handleBackspace={handleBackspace}
            handleKeyboardTypeSwitch={handleKeyboardTypeSwitch}
            handleSearch={handleSearch}
            languageKey={activeKeyboardLanguage}
            languageSwitchButton={
              !isLatinLanguage ? (
                <LanguageSwitchButton onClick={handleLanguageSwitch} />
              ) : null
            }
            showSearchKey={showSearchKey}
          />
        ) : keyboardType === "symbol" ? (
          <SymbolKeyboard
            handleClick={handleClick}
            handleBackspace={handleBackspace}
            handleKeyboardTypeSwitch={handleKeyboardTypeSwitch}
            handleSearch={handleSearch}
            languageKey={activeKeyboardLanguage}
            languageSwitchButton={
              !isLatinLanguage ? (
                <LanguageSwitchButton onClick={handleLanguageSwitch} />
              ) : null
            }
            showSearchKey={showSearchKey}
          />
        ) : null}
      </div>
      <div className={styles.closeIcon}>
        <IconButton
          size="regular"
          icon="close"
          iconSize={24}
          type="primary"
          dataQA="search-close"
          onClick={onClose}
        />
      </div>
    </div>
  );
};

export default Keyboard;
